import { FORM } from 'constants/contentTypes'
import dayjs from 'utils/dayjsSetup'

import { saveFile } from 'helpers'

const DATE_FORMAT = 'MMDDYYYY'

/**
 * @description Settings for controller
 * @module API controller / Profiles
 */
module.exports = {
  adminUpdateEmail: {
    /**
     * @description Get request settings (Edit Profile)
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.id - The profile ID.
     * @param  {array} options.body - Fields to edit.
     * @return {object} Request Settings
     */
    settings: ({ accountId, email, profileId }) => ({
      endpoint: `/account/${accountId}/profiles/admin_update_email`,
      body: {
        profileId,
        email,
      },
      method: 'PATCH',
    }),
  },
  editEmail: {
    /**
     * @description Get request settings (Edit Profile)
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.id - The profile ID.
     * @param  {array} options.body - Fields to edit.
     * @return {object} Request Settings
     */
    settings: ({ accountId, email }) => ({
      endpoint: `/account/${accountId}/profiles/confirm_email`,
      body: {
        email,
      },
      method: 'POST',
    }),
  },

  updateEmailValidateCode: {
    /**
     * @description Get request settings (Edit Profile)
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.id - The profile ID.
     * @param  {array} options.body - Fields to edit.
     * @return {object} Request Settings
     */
    settings: ({ accountId, body }) => ({
      endpoint: `/account/${accountId}/profiles/update_email`,
      body,
      method: 'PATCH',
    }),
  },

  editProfile: {
    /**
     * @description Get request settings (Edit Profile)
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.id - The profile ID.
     * @param  {array} options.body - Fields to edit.
     * @return {object} Request Settings
     */
    settings: ({ accountId, id, ...body }) => ({
      endpoint: `/account/${accountId}/profiles/${id}`,
      body,
      method: 'PUT',
    }),
  },

  deleteProfile: {
    /**
     * @description Get request settings (Edit Profile)
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.profileId - The profile ID.
     * @return {object} Request Settings
     */
    settings: ({ accountId, profileId }) => ({
      endpoint: `/account/${accountId}/profiles/${profileId}`,
      method: 'DELETE',
    }),
  },

  updateAvatar: {
    /**
     * Get request settings (Update Avatar endpoint)
     * @param  {object} options
     * @param {number} options.accountId
     * @param {object} options.avatar
     * @return {object} Request Settings
     */
    settings({ accountId, avatar }) {
      const form = new FormData()

      if (avatar) {
        form.append('avatar', avatar)
      }

      return {
        endpoint: `/account/${accountId}/profiles/set_avatar`,
        body: form,
        headers: {
          enctype: FORM,
        },
        method: 'PATCH',
      }
    },
  },

  getMyNetwork: {
    ignoreNotFound: true,

    /**
     * Request settings (Gets my network data with search and pagination)
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {boolean} options.includeAll - Include privated and not connected profiles
     * @param  {boolean} options.limitedMember - Filter by limited_member flag (true/false). Omit for all members.
     * @param  {number} options.pageIndex - The page index.
     * @param  {string} options.search - The filter to search.
     * @param  {number} options.teamId - Team ID
     * @return {object} request settings
     */
    settings({ accountId, includeAll, limitedMember, pageIndex, search, teamId }) {
      const { filterBy, value: searchValue } = search || {}

      let endpoint = `/account/${accountId}/profiles/my_network`
      const cleanSearch = searchValue ? searchValue.trim() : null
      const searchBy = searchValue ? filterBy : null
      const queryParams = []

      if (teamId) {
        endpoint = `/account/${accountId}/teams/${teamId}/non_members`
      }

      if (includeAll) {
        endpoint = `/account/${accountId}/profiles/all_network`
      }

      if (pageIndex) {
        endpoint += `/page/${pageIndex}`
      }

      if (searchBy) {
        endpoint += `/search_field/${encodeURIComponent(searchBy)}`
      }

      if (cleanSearch) {
        endpoint += `/search/${encodeURIComponent(cleanSearch)}`
      }

      if (limitedMember !== undefined && limitedMember !== null) {
        queryParams.push(`limited_member=${limitedMember}`)
      }

      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`
      }

      return {
        endpoint,
        method: 'GET',
      }
    },
  },

  getMyConnections: {
    ignoreNotFound: true,

    /**
     * Request settings (Gets my connections data with search and pagination)
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.pageIndex - The page index.
     * @param  {string} options.search - The filter to search.
     * @return {object} request settings
     */
    settings({ accountId, pageIndex, search }) {
      const { filterBy, value: searchValue } = search || {}

      let endpoint = `/account/${accountId}/profiles/my_connections`
      const cleanSearch = searchValue ? searchValue.trim() : null
      const searchBy = searchValue ? filterBy : null

      if (pageIndex) {
        endpoint += `/page/${pageIndex}`
      }

      if (searchBy) {
        endpoint += `/search_field/${encodeURIComponent(searchBy)}`
      }

      if (cleanSearch) {
        endpoint += `/search/${encodeURIComponent(cleanSearch)}`
      }

      return {
        endpoint,
        method: 'GET',
      }
    },
  },

  setPrivacy: {
    /**
     * @description Request settings - Sets account privacy
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {boolean} options.isPublic - The value to patch.
     * @return {object} request settings
     */
    settings: ({ accountId, isPublic }) => ({
      endpoint: `/account/${accountId}/profiles/set_privacy`,
      body: {
        public: isPublic,
      },
      method: 'PATCH',
    }),
  },

  getNetworkDetails: {
    /**
     * Gets information of a specific profile
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.profileId - The profile ID.
     * @return {object} API Call to get the profile information
     */
    settings: ({ accountId, profileId }) => ({
      endpoint: `/account/${accountId}/profiles/network_profile/${profileId}`,
      method: 'GET',
    }),
  },

  completeSSOProvision: {
    encrypt: {
      useOptionsToken: true,
    },
    /**
     * Completes the user information for a SSO account profile
     * @param {number} accountId
     * @param {object} profile
     * @param {string} ssoToken
     * @return {object} API Call to get the profile information
     */
    settings: ({ accountId, profile, ssoToken }) => ({
      body: { profile },
      endpoint: `/account/${accountId}/profiles/provision`,
      headers: {
        Authorization: `Token sso=${ssoToken}`,
      },
      method: 'POST',
    }),
  },

  exportMembers: {
    afterResponse(originalResponse, onFinish) {
      saveFile(originalResponse, this.fileName, onFinish)
    },

    /**
     * @description Request settings (Download Energy Map Image)
     * @param  {number} accountId - The account ID.
     * @returns {object} - Request settings.
     */
    settings(accountId) {
      this.fileName = `members_${dayjs().format(DATE_FORMAT)}.csv`

      return {
        method: 'GET',
        headers: {
          enctype: FORM,
        },
        responseType: 'blob',
        endpoint: `/account/${accountId}/profiles/download_csv`,
      }
    },
  },

  exportNotes: {
    afterResponse(originalResponse, onFinish) {
      saveFile(originalResponse, this.fileName, onFinish)
    },

    /**
     * @description Request settings (Download Energy Map Image)
     * @param  {number} accountId - The account ID.
     * @returns {object} - Request settings.
     */
    settings({ accountId, profileId }) {
      this.fileName = `notes_${dayjs().format(DATE_FORMAT)}.csv`

      return {
        method: 'GET',
        headers: {
          enctype: FORM,
        },
        responseType: 'blob',
        endpoint: `/account/${accountId}/profiles/download_csv_notes/${profileId}`,
      }
    },
  },

  bulkDelete: {
    /**
     * @description Get request settings (send bulk delete file)
     * @param {object} options
     * @param {number} options.accountId
     * @param {object} options.file
     * @returns {object} request settings
     */
    settings({ accountId, file }) {
      const form = new FormData()

      form.append('file', file)

      return {
        endpoint: `/account/${accountId}/profiles/bulk_delete`,
        body: form,
        headers: {
          enctype: FORM,
        },
        method: 'POST',
      }
    },
  },

  createProfileNote: {
    /**
     * Gets notes of a specific profile
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.profileNote - Note to create.
     * @return {object} API Call to create a profile note
     */
    settings: ({ accountId, profileNote }) => ({
      body: {
        ...profileNote,
      },
      endpoint: `/account/${accountId}/profiles/create_profile_note`,
      method: 'POST',
    }),
  },

  deleteProfileNote: {
    /**
     * Gets notes of a specific profile
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.noteId - The note ID.
     * @return {object} API Call to delete a profile note
     */
    settings: ({ accountId, noteId }) => ({
      endpoint: `/account/${accountId}/profiles/destroy_profile_note/${noteId}`,
      method: 'DELETE',
    }),
  },

  getProfileNotes: {
    /**
     * Gets notes of a specific profile
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.profileId - The profile ID.
     * @return {object} API Call to get the profile notes
     */
    settings: ({ accountId, profileId }) => ({
      endpoint: `/account/${accountId}/profiles/get_profile_notes/${profileId}`,
      method: 'GET',
    }),
  },

  updateProfileNote: {
    /**
     * Gets notes of a specific profile
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.profileId - The profile ID.
     * @return {object} API Call to update a profile note
     */
    settings: ({ accountId, profileNote, noteId }) => ({
      endpoint: `/account/${accountId}/profiles/update_profile_note/${noteId}`,
      body: profileNote,
      method: 'PATCH',
    }),
  },

  getProfileLanguages: {
    /**
     * Gets information of a specific profile
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @return {object} API Call to get the profile languages available for setting up profile
     */
    settings: () => ({
      endpoint: '/profiles/languages',
      method: 'GET',
    }),
  },

  completeInterstitial: {
    /**
     * @description Marks the activation interstitial as complete for the current user's profile.
     * @param  {object} options
     * @param  {number} options.accountId     - The account ID.
     * @param  {Array}  options.stepsSkipped  - Step keys the user skipped, e.g. ['network_reveal'].
     * @param  {boolean} options.optedOutEmails - Whether the user opted out of non-transactional emails.
     * @returns {object} - Request settings.
     */
    settings: ({ accountId, stepsSkipped, optedOutEmails }) => ({
      endpoint: `/account/${accountId}/profiles/complete_interstitial`,
      body: { steps_skipped: stepsSkipped, opted_out_emails: optedOutEmails },
      method: 'POST',
    }),
  },

  networkReveal: {
    /**
     * @description Returns assessed colleagues and platform/org counts for the Network Reveal interstitial step.
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.offset    - 0-based pagination offset (default 0 = initial load).
     * @returns {object} - Request settings.
     */
    settings: ({ accountId, offset = 0 }) => ({
      endpoint: `/account/${accountId}/profiles/network_reveal?offset=${offset}`,
      method: 'GET',
    }),
  },

  workingCircle: {
    /**
     * @description Returns the current user's working circle members.
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @returns {object} - Request settings.
     */
    settings: ({ accountId }) => ({
      endpoint: `/account/${accountId}/profiles/working_circle`,
      method: 'GET',
    }),
  },

  addWorkingCircleMember: {
    /**
     * @description Adds a profile to the current user's working circle.
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.profileId - The profile ID to add to the circle.
     * @returns {object} - Request settings.
     */
    settings: ({ accountId, profileId }) => ({
      endpoint: `/account/${accountId}/profiles/working_circle`,
      body: { profile_id: profileId },
      method: 'POST',
    }),
  },

  removeWorkingCircleMember: {
    /**
     * @description Removes a profile from the current user's working circle.
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {number} options.profileId - The profile ID to remove from the circle.
     * @returns {object} - Request settings.
     */
    settings: ({ accountId, profileId }) => ({
      endpoint: `/account/${accountId}/profiles/working_circle/${profileId}`,
      method: 'DELETE',
    }),
  },

  searchProfiles: {
    /**
     * @description Searches profiles in the account by name or email (min 2 chars, assessed only).
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @param  {string} options.q         - Search query (at least 2 characters).
     * @returns {object} - Request settings.
     */
    settings: ({ accountId, q }) => ({
      endpoint: `/account/${accountId}/profiles/search?q=${encodeURIComponent(q)}`,
      method: 'GET',
    }),
  },

  pairReportSuggestion: {
    /**
     * @description Returns the best colleague to pre-select on the Pair Report interstitial step.
     *   Priority: working circle member with scores > most recently assessed colleague.
     *   Returns { suggested_profile: { id, first_name, last_name, avatar_url } } or null.
     * @param  {object} options
     * @param  {number} options.accountId - The account ID.
     * @returns {object} - Request settings.
     */
    settings: ({ accountId }) => ({
      endpoint: `/account/${accountId}/profiles/pair_report_suggestion`,
      method: 'GET',
    }),
  },
}
